import type { ISettingsProps } from "./Models.ts";
import { Operations } from "./Constants.ts";
import { ladderFor, levelName } from "./FactLevels.ts";
import { gradeName, wholePageSettings } from "./SubPack.ts";
import type { PackSpec } from "../Shared/Seo/routeSeo.ts";
import type { SheetSpec } from "./PdfGenerator/sheetSpec.ts";

/**
 * The catch-up pack of a placement check (ADR 0086 §5): the pages a teacher chose from the skills a child
 * missed, each one whole sheet with its key, behind a cover. It is a pack built from the check's own
 * strands - the same pages and the same one-page treatment the substitute packs use (`wholePageSettings`) -
 * so nothing here draws a problem, and every sheet is a page a teacher could print from its own address.
 *
 * What was chosen is **not in the pack's ID**, and it is the only thing that is not. The choice is a list of
 * the skills a child missed, which is a fact about a child, and 0011 §1 and §5 keep that out of the link, the
 * storage and the event. So the ID names the draw of the problems and not the pages; typing it into Rebuild
 * does not rebuild a catch-up pack, and the page does not offer it.
 *
 * Up to four sheets: a child given a dozen pages is not given a plan. The fact practice is a sheet like the
 * others, a level test of the ladder the check's fact finder placed the child on, at the level the teacher
 * chose from "Where to start".
 */
export const CATCH_UP_MAX = 4;

export interface CatchUpChoice {
    /** Indexes of the check's activities: its strands, and its stories. Not the fact finder's. */
    strands: number[];
    /** A level of the fact finder's ladder to practise, or null for none. */
    finderLevel: number | null;
}

/** How many sheets a choice makes, for the control that counts them. */
export function catchUpCount(choice: CatchUpChoice): number {
    return choice.strands.length + (choice.finderLevel !== null ? 1 : 0);
}

export function catchUpSheets(pack: PackSpec, choice: CatchUpChoice, seed: string): SheetSpec[] {
    const check = pack.check;
    if (!check) {
        throw new Error("Catch-up pack: the pack has no `check`");
    }
    const count = catchUpCount(choice);
    if (count < 1 || count > CATCH_UP_MAX) {
        throw new Error(`Catch-up pack: ${count} sheets; choose one to ${CATCH_UP_MAX}`);
    }
    const strands = [...new Set(choice.strands)].sort((a, b) => a - b);
    for (const index of strands) {
        if (index < 0 || index > check.stories) {
            throw new Error(`Catch-up pack: activity ${index} is not a skill of this check`);
        }
    }

    const items: { text: string; settings: ISettingsProps }[] = strands.map((index) => {
        const activity = pack.activities[index];
        const name = activity.name ?? activity.slug;
        // The stories' own blurb says "four stories", which is the check's page of them; a catch-up page of
        // stories is the page's own whole sheet.
        const line = index === check.stories ? "a page of stories, so the child has to choose the operation" : activity.blurb.replace(/\.$/, "").toLowerCase();
        return { text: `${name}: ${line}`, settings: wholePageSettings(activity) };
    });

    if (choice.finderLevel !== null) {
        const finder = check.factFinder;
        const ladder = ladderFor(finder.operations as Operations[], 10);
        const level = ladder?.levels.find((candidate) => candidate.id === choice.finderLevel);
        if (!ladder || !level) {
            throw new Error(`Catch-up pack: no level ${choice.finderLevel} on this check's fact ladder`);
        }
        items.push({
            text: `Fact practice: level ${level.id}, ${levelName(level, ladder.id)}`,
            settings: wholePageSettings(pack.activities[finder.activity], {
                operations: finder.operations,
                factLevel: level.id,
                factLevelCeiling: 10,
            }),
        });
    }

    const sheets: SheetSpec[] = items.map((item, index) => ({
        settings: item.settings,
        seed: `${seed}:${index}`,
        footerSeed: `${seed}-${index}`,
    }));
    const cover: SheetSpec = {
        // Not read: a cover draws no problems.
        settings: items[0].settings,
        seed,
        footerSeed: seed,
        catchUpCover: {
            grade: gradeName(pack.grade),
            reviews: pack.reviews,
            id: seed,
            items: items.map((item) => item.text),
            copyPages: count === 1 ? "2" : `2 to ${count + 1}`,
            keyPages: count === 1 ? "3" : `${count + 2} to ${2 * count + 1}`,
        },
    };
    return [cover, ...sheets];
}
